// ClinicOS EHR — Keycloak OIDC Auth Hook
// Wraps keycloak-js for login/logout/token management
// Falls back to demo mode if Keycloak is unavailable

import { createContext, useContext, useState, useEffect, useCallback, type ReactNode } from 'react';

interface User {
    id: string;
    name: string;
    email: string;
    role: 'doctor' | 'coordinator' | 'receptionist' | 'admin';
    firstName: string;
    lastName: string;
}

interface AuthState {
    user: User | null;
    isAuthenticated: boolean;
    isLoading: boolean;
    token: string | null;
    login: () => void;
    logout: () => void;
}

const AuthContext = createContext<AuthState>({
    user: null,
    isAuthenticated: false,
    isLoading: true,
    token: null,
    login: () => { },
    logout: () => { },
});

// Demo user for local development
const DEMO_USER: User = {
    id: 'dr_001',
    name: 'Dr. Demo Médico',
    email: 'demo@clinicos.app',
    role: 'doctor',
    firstName: 'Demo',
    lastName: 'Médico',
};

const KEYCLOAK_URL = import.meta.env.VITE_KEYCLOAK_URL || 'http://localhost:8180';
const KEYCLOAK_REALM = import.meta.env.VITE_KEYCLOAK_REALM || 'clinicos';
const KEYCLOAK_CLIENT_ID = import.meta.env.VITE_KEYCLOAK_CLIENT_ID || 'clinicos-ehr';

export function AuthProvider({ children }: { children: ReactNode }) {
    const [user, setUser] = useState<User | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [token, setToken] = useState<string | null>(null);

    useEffect(() => {
        initAuth();
    }, []);

    async function initAuth() {
        // In production/demo mode (VITE_API_URL is set), skip Keycloak entirely
        if (import.meta.env.VITE_API_URL || import.meta.env.DEV) {
            // In development or demo mode, skip Keycloak and use demo identity immediately
            console.info('🔓 Demo mode — using demo user (API proxy to middleware)');
            setUser(DEMO_USER);
            setToken('demo-token');
            setIsLoading(false);
            return;
        }

        // Check if Keycloak is available (local development only)
        try {
            const kcCheck = await fetch(`${KEYCLOAK_URL}/realms/${KEYCLOAK_REALM}`, {
                signal: AbortSignal.timeout(3000),
            });

            if (!kcCheck.ok) throw new Error('Keycloak unreachable');

            // Try to get token from URL hash (OIDC redirect callback)
            const hash = window.location.hash;
            if (hash.includes('access_token=')) {
                const params = new URLSearchParams(hash.substring(1));
                const accessToken = params.get('access_token');
                if (accessToken) {
                    setToken(accessToken);
                    // Decode JWT payload to get user info
                    const payload = JSON.parse(atob(accessToken.split('.')[1]));
                    setUser({
                        id: payload.sub,
                        name: payload.name || payload.preferred_username,
                        email: payload.email || '',
                        role: (payload.realm_access?.roles?.find((r: string) =>
                            ['doctor', 'coordinator', 'receptionist', 'admin'].includes(r)
                        ) || 'doctor') as User['role'],
                        firstName: payload.given_name || '',
                        lastName: payload.family_name || '',
                    });
                    // Clean URL
                    window.history.replaceState(null, '', window.location.pathname);
                }
            } else {
                // Check for existing session via stored token
                const storedToken = sessionStorage.getItem('clinicos_token');
                if (storedToken) {
                    try {
                        const payload = JSON.parse(atob(storedToken.split('.')[1]));
                        if (payload.exp * 1000 > Date.now()) {
                            setToken(storedToken);
                            setUser({
                                id: payload.sub,
                                name: payload.name || payload.preferred_username,
                                email: payload.email || '',
                                role: (payload.realm_access?.roles?.find((r: string) =>
                                    ['doctor', 'coordinator', 'receptionist', 'admin'].includes(r)
                                ) || 'doctor') as User['role'],
                                firstName: payload.given_name || '',
                                lastName: payload.family_name || '',
                            });
                        }
                    } catch {
                        sessionStorage.removeItem('clinicos_token');
                    }
                }
            }
        } catch {
            // Keycloak unavailable — use demo mode
            console.info('🔓 Keycloak unavailable — using demo mode');
            setUser(DEMO_USER);
            setToken('demo-token');
        }

        setIsLoading(false);
    }

    const login = useCallback(() => {
        const redirectUri = encodeURIComponent(window.location.origin + '/');
        const authUrl = `${KEYCLOAK_URL}/realms/${KEYCLOAK_REALM}/protocol/openid-connect/auth?client_id=${KEYCLOAK_CLIENT_ID}&response_type=token&redirect_uri=${redirectUri}&scope=openid profile email`;
        window.location.href = authUrl;
    }, []);

    const logout = useCallback(() => {
        sessionStorage.removeItem('clinicos_token');
        setUser(null);
        setToken(null);
        const redirectUri = encodeURIComponent(window.location.origin + '/');
        window.location.href = `${KEYCLOAK_URL}/realms/${KEYCLOAK_REALM}/protocol/openid-connect/logout?post_logout_redirect_uri=${redirectUri}&client_id=${KEYCLOAK_CLIENT_ID}`;
    }, []);

    // Store token in session storage
    useEffect(() => {
        if (token && token !== 'demo-token') {
            sessionStorage.setItem('clinicos_token', token);
        }
    }, [token]);

    return (
        <AuthContext.Provider value={{ user, isAuthenticated: !!user, isLoading, token, login, logout }}>
            {children}
        </AuthContext.Provider>
    );
}

export function useAuth() {
    return useContext(AuthContext);
}

export function LoginPage({ onLogin }: { onLogin: () => void }) {
    return (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '100dvh', background: 'var(--color-bg)' }}>
            <div style={{ textAlign: 'center', maxWidth: 400, padding: 40 }}>
                <div style={{ fontSize: 48, marginBottom: 16 }}>🏥</div>
                <h1 style={{ fontSize: 24, fontWeight: 700, marginBottom: 8, color: 'var(--color-primary)' }}>ClinicOS EHR</h1>
                <p style={{ color: 'var(--color-text-secondary)', marginBottom: 32 }}>Sistema de Historia Clínica Electrónica</p>
                <button onClick={onLogin} style={{
                    background: 'var(--color-primary)', color: '#fff', border: 'none', padding: '12px 32px',
                    borderRadius: 'var(--radius-lg)', fontSize: 16, fontWeight: 600, cursor: 'pointer',
                    transition: '150ms', width: '100%',
                }}>
                    Iniciar Sesión
                </button>
                <p style={{ fontSize: 12, color: 'var(--color-text-muted)', marginTop: 16 }}>
                    Autenticación vía Keycloak OIDC
                </p>
            </div>
        </div>
    );
}
